<template>
  <CDropdown variant="nav-item">
    <CDropdownToggle class="text-white" :caret="false" aria-label="Notifications">
      <span class="d-inline-block my-1 mx-2 position-relative">
        <CIcon icon="cil-bell" size="lg" />
      </span>
    </CDropdownToggle>
    <CDropdownMenu class="pt-0">
      <CDropdownHeader v-if="details.name && details.release"
        class="dropdown-header bg-light dark:bg-white dark:bg-opacity-10">
        <strong>{{ details.name }} - {{ details.release }}</strong>
      </CDropdownHeader>
      <CDropdownItem class="d-block mt-2" disabled>
        <CAlert color="light" class="d-flex align-items-center">
          <CIcon icon="cil-warning" class="flex-shrink-0 me-2" width="24" height="24" />
          <div>The current data are instantaneous.</div>
        </CAlert>
      </CDropdownItem>
      <CDropdownItem v-if="
        details.hostname ||
        details.name ||
        details.uptime ||
        details.certificate
      " class="d-block" disabled>
        <div class="mb-1">
          <div v-if="details.hostname">
            <small><b>Hostname</b> {{ details.hostname }}</small>
          </div>
          <div v-if="details.name">
            <small><b>Name</b> {{ details.name }}</small>
          </div>
          <div v-if="details.uptime">
            <small><b>Uptime</b> {{ duration(details.uptime) }}</small>
          </div>
          <div v-if="details.certificate">
            <small><b>Certificate</b> {{ details.certificate }}</small>
          </div>
        </div>
      </CDropdownItem>
      <!-- A load average counts processes wanting a core, not a percentage:
           the bar shows it shared over the cores. -->
      <CDropdownItem v-if="details.loadavg" class="d-block" disabled>
        <div class="d-flex align-items-baseline mb-1">
          <small class="me-auto"><b>CPU load</b></small>
          <small class="text-body-secondary">
            {{ details.loadavg[0].toFixed(2) }}<template v-if="cores"> of {{ cores }}</template>
          </small>
        </div>
        <CProgress thin :color="loadColor" :value="load" />
        <div v-if="Array.isArray(details.cpus)">
          <div v-for="(item, index) in details.cpus" :key="index">
            <small class="text-body-secondary">{{ item.model }}</small>
          </div>
        </div>
      </CDropdownItem>
      <CDropdownItem v-if="details.memusage" class="d-block" disabled>
        <div class="mb-1">
          <small><b>RAM Usage</b></small>
        </div>
        <CProgress thin :color="details.memusage > 90 ? 'danger' : 'primary'"
          :value="details.memusage" />
        <small v-if="details.totalmem && details.freemem" class="text-body-secondary">{{
          formatBytes(details.totalmem - details.freemem, 1) }}
          /
          {{ formatBytes(details.totalmem, 1) }}</small>
      </CDropdownItem>
    </CDropdownMenu>
  </CDropdown>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { duration, formatBytes } from '@/libraries/utilities'
import type { define as Tenant } from '@/components/tenant/interfaces'

/* arrange() copies the ping collector's keys as reported over the tenant's name and
 * certificate; any field may be missing. */
interface PingDetails {
  name?: string
  release?: string
  hostname?: string
  certificate?: string
  uptime?: number
  loadavg?: number[]
  cpus?: Array<{ model?: string }>
  memusage?: number
  totalmem?: number
  freemem?: number
}

export default defineComponent({
  name: 'HeaderDropdownNotify',
  props: {
    tenant: { type: Object as PropType<Tenant>, required: true },
  },
  data() {
    return {
      details: {} as PingDetails,
    }
  },
  computed: {
    cores(): number {
      return Array.isArray(this.details.cpus) ? this.details.cpus.length : 0
    },
    load(): number {
      const average = this.details.loadavg?.[0] ?? 0
      if (!this.cores) return Math.min(1e2, average * 1e2)
      return Math.min(1e2, Math.round((average * 1e2) / this.cores))
    },
    loadColor(): string {
      return this.load > 90 ? 'danger' : this.load > 70 ? 'warning' : 'success'
    },
  },
  watch: {
    tenant: {
      handler: 'arrange',
      immediate: true,
    },
  },
  methods: {
    duration,
    formatBytes,
    arrange() {
      const details: PingDetails = {}
      const { certificate, name, linked } = this.tenant
      if (name) details.name = name
      if (certificate) details.certificate = certificate
      const ping = linked?.garbage?.ping
      if (ping) {
        Object.keys(ping).forEach((item) => {
          ;(details as Record<string, unknown>)[item] = ping[item]?.data
        })
      }
      this.details = details
    },
  },
})
</script>
